import React, { useRef, useState, useEffect } from 'react'
import Slider from 'react-slick'
import { IProps } from './index'
import { Text } from 'marketing-site/src/library/elements/Text'
import { RichText } from 'marketing-site/src/library/elements/RichText'
import { Image } from 'marketing-site/src/library/elements/Image'
import { mq, LegacyColor, fontGraphik } from '../../utils'
import { NewBrandingHoc } from '../../utils/NewBrandingHoc'
import { Carousel as CarouselDefault } from './component'

export function CarouselLegacy(props: IProps) {
  const [mounted, setMounted] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(1)
  const slider = useRef<Slider>(null)
  const controlsSlider = useRef<Slider>(null)

  useEffect(() => {
    // Slick can't calculate correctly size if we init it before page load
    setMounted(true)
  }, [])

  const sliderSettings = {
    arrows: false,
    dots: true,
    infinite: false,
    speed: 200,
    centerMode: true,
    centerPadding: '25%',
    initialSlide: 1,
    asNavFor: controlsSlider.current || undefined,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setCurrentSlide(nextSlide)
      controlsSlider.current?.slickGoTo(nextSlide)
    },
  }

  const controlsSettings = {
    arrows: false,
    dots: false,
    infinite: false,
    speed: 200,
    initialSlide: 1,
    slidesToShow: 3,
    asNavFor: slider.current || undefined,

    ...(props.isMobile && {
      centerMode: true,
      centerPadding: '30px',
      slidesToShow: 1,
    }),
  }

  return (
    <>
      <section className="section--carousel">
        <div className="carousel-content">
          <h2>
            <Text size="xxl+">{props.headline}</Text>
          </h2>

          <div className="carousel-main">
            <Slider ref={slider} {...sliderSettings}>
              {props.slides.map((slide, index) => (
                <div key={slide.id}>
                  {/* eslint-disable-next-line */}
                  <div
                    className="carousel-slide"
                    data-testid="carousel-slide"
                    style={{
                      backgroundColor: slide.backgroundColor || 'transparent',
                    }}
                    onClick={() => {
                      slider.current?.slickGoTo(index)
                      controlsSlider.current?.slickGoTo(index)
                    }}
                  >
                    <div
                      className={`carousel-image-wrap carousel-image-wrap--${index}`}
                      style={{ maxWidth: slide.imageWidth || 'none', margin: '0 auto' }}
                    >
                      <Image {...slide.image} />
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          {mounted && (
            <div className="carousel-controls">
              <Slider ref={controlsSlider} {...controlsSettings}>
                {props.slides.map(
                  (slide, index) =>
                    slide.description && (
                      <div key={slide.id}>
                        {/* eslint-disable-next-line */}
                        <div
                          onClick={() => {
                            slider.current?.slickGoTo(index)
                            controlsSlider.current?.slickGoTo(index)
                          }}
                          className={`carousel-description ${
                            currentSlide === index ? 'carousel-description-active' : null
                          }`}
                        >
                          <RichText document={slide.description} />
                        </div>
                      </div>
                    ),
                )}
              </Slider>
            </div>
          )}
        </div>
      </section>

      <style jsx>{`
        .section--carousel {
          margin-top: 100px;
          margin-bottom: 100px;
          min-width: 100%;
        }

        .carousel-content {
          width: 1290px;
          max-width: 100%;
          margin: 0 auto;
        }

        h2 {
          text-align: center;
          margin: 0 auto 60px;
        }

        .carousel-slide {
          margin: 0 20px;
          text-align: center;
          cursor: pointer;
          border-radius: 25px;
          overflow: hidden;
        }

        :global(.slick-active) .carousel-slide {
          cursor: default;
        }

        .section--carousel :global(.slick-dots) {
          margin: 20px auto 0;
          text-align: center;
        }

        .section--carousel :global(.slick-dots li) {
          display: inline-block;
        }

        .section--carousel :global(.slick-dots button) {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${LegacyColor.UIGray};
          font-size: 0;
          margin: 0 5px;
          cursor: pointer;
        }

        .section--carousel :global(.slick-dots .slick-active button) {
          background-color: ${LegacyColor.Black};
        }

        .carousel-controls {
          margin: 40px auto 0;
          max-width: 1040px;
        }

        .carousel-description {
          border: 2px solid black;
          border-radius: 8px;
          padding: 15px;
          margin: 0 10px;
          cursor: pointer;
          transition: color 0.2s, background 0.2s;
        }

        .carousel-description-active,
        .carousel-description:hover {
          color: white;
          background: black;
        }

        .carousel-description :global(p) {
          margin: 0;
        }

        .carousel-description :global(b) {
          font-family: ${fontGraphik};
        }

        @media (max-width: 767px) {
          .carousel-slide {
            background: none !important;
          }
        }

        @media (${mq.tablet}) {
          .carousel-slide {
            padding: 0 40px;
            margin: 0 20px;
          }

          .carousel-description {
            padding: 15px 30px 15px 20px;
            max-width: 340px;
            width: auto;
          }

          .carousel-controls {
            padding: 0 20px;
            margin-top: 60px;
          }
        }

        @media (${mq.desktop}) {
          .carousel-slide {
            margin: 0 45px;
          }
        }

        @media (min-width: 1290px) {
          .carousel-main {
            :global(.slick-slider:before),
            :global(.slick-slider:after) {
              content: '';
              width: 100px;
              background: linear-gradient(90deg, white 0%, white 30%, rgba(0, 0, 0, 0) 100%);
              position: absolute;
              top: 0;
              left: 0;
              bottom: 0;
              z-index: 2;
              pointer-events: none;
            }

            :global(.slick-slider:after) {
              left: auto;
              right: 0;
              background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, white 70%);
            }
          }
        }
      `}</style>
    </>
  )
}

export const Carousel = NewBrandingHoc(CarouselLegacy, CarouselDefault)
